export type Validator<T> = (data: T) => Partial<Record<keyof T, string>>;

export const composeValidators = <T>(...validators: Array<Validator<T>>): Validator<T> => {
  return (data: T) => {
    return validators.reduce((acc, validator) => {
      const result = validator(data);
      return { ...acc, ...result };
    }, {} as Partial<Record<keyof T, string>>);
  };
};

export const requiredString = <T>(field: keyof T, label: string): Validator<T> => {
  return (data: T) => {
    const value = (data as any)[field] as string | undefined;
    if (!value || value.trim().length === 0) {
      return { [field]: `${label} is required` } as Partial<Record<keyof T, string>>;
    }
    return {};
  };
};

export const positiveIntegerOrZero = <T>(field: keyof T, label: string): Validator<T> => {
  return (data: T) => {
    const raw = (data as any)[field];
    const value = typeof raw === 'string' ? parseInt(raw, 10) : raw;
    if (Number.isNaN(value) || value < 0) {
      return { [field]: `${label} must be 0 or a positive number` } as Partial<Record<keyof T, string>>;
    }
    return {};
  };
};

export const dateOrder = <T>(startField: keyof T, endField: keyof T, message: string): Validator<T> => {
  return (data: T) => {
    const start = (data as any)[startField] as Date | undefined;
    const end = (data as any)[endField] as Date | undefined;
    if (start && end && start >= end) {
      return { [endField]: message } as Partial<Record<keyof T, string>>;
    }
    return {};
  };
};

export const requireIf = <T>(
  predicate: (data: T) => boolean,
  field: keyof T,
  label: string,
): Validator<T> => {
  return (data: T) => {
    if (!predicate(data)) return {};
    const value = (data as any)[field] as any;
    if (value === undefined || value === null || (typeof value === 'string' && value.trim().length === 0)) {
      return { [field]: `${label} is required` } as Partial<Record<keyof T, string>>;
    }
    return {};
  };
};

export const emailFormat = <T>(field: keyof T): Validator<T> => {
  return (data: T) => {
    const value = (data as any)[field] as string | undefined;
    if (!value) return {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return { [field]: 'Invalid email format' } as Partial<Record<keyof T, string>>;
    }
    return {};
  };
};

export const phoneFormat = <T>(field: keyof T): Validator<T> => {
  return (data: T) => {
    const raw = (data as any)[field] as string | undefined;
    if (!raw) return {};
    const value = raw.replace(/\s/g, '');
    const phoneRegex = /^\d{10}$|^\d{3}-\d{3}-\d{4}$/;
    if (!phoneRegex.test(value)) {
      return { [field]: 'Invalid phone number format' } as Partial<Record<keyof T, string>>;
    }
    return {};
  };
};

export const requireArrayLength = <T>(field: keyof T, label: string, minLength: number = 1): Validator<T> => {
  return (data: T) => {
    const value = (data as any)[field] as any[] | undefined;
    if (!value || !Array.isArray(value) || value.length < minLength) {
      return { [field]: `Please select at least ${minLength === 1 ? 'one' : minLength} ${label.toLowerCase()}` } as Partial<Record<keyof T, string>>;
    }
    return {};
  };
};

export const requireArrayLengthIf = <T>(
  predicate: (data: T) => boolean,
  field: keyof T,
  label: string,
  minLength: number = 1
): Validator<T> => {
  return (data: T) => {
    if (!predicate(data)) return {};
    const value = (data as any)[field] as any[] | undefined;
    if (!value || !Array.isArray(value) || value.length < minLength) {
      return { [field]: `Please select at least ${minLength === 1 ? 'one' : minLength} ${label.toLowerCase()}` } as Partial<Record<keyof T, string>>;
    }
    return {};
  };
};

export const validateData = <T, E extends Partial<Record<keyof T, string>>>(
  data: T,
  setErrors: React.Dispatch<React.SetStateAction<E>>,
  validator: Validator<T>,
): boolean => {
  const errors = validator(data);
  const hasErrors = Object.keys(errors).length > 0;
  setErrors(errors as E);
  return !hasErrors;
};


